import { apiRequest } from '@/lib/api/client';
import type {
  ClientAISuggestion,
  ClientCalendarEvent,
  ClientConnection,
  ClientFile,
  ClientJointArticle,
  ClientMessage,
  ClientNotebookNote,
  ClientOverviewStats,
  ClientProfile,
  ClientProject,
  ClientProjectStage,
  ClientRequest,
  ClientRequestClarification,
  ClientToolRunResult,
  ClientWorkspaceSummary,
} from './types';

export const getClientSummary = (): Promise<ClientWorkspaceSummary> =>
  apiRequest<ClientWorkspaceSummary>('/client/summary', { channel: 'platform' });

export const getClientProfile = (): Promise<ClientProfile> =>
  apiRequest<ClientProfile>('/client/profile', { channel: 'platform' });

export const updateClientProfile = (payload: Partial<ClientProfile>): Promise<ClientProfile> =>
  apiRequest<ClientProfile>('/client/profile', {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const getClientStats = (): Promise<ClientOverviewStats> =>
  apiRequest<ClientOverviewStats>('/client/stats', { channel: 'platform' });

export const getClientRequests = (
  status?: string,
  serviceType?: string
): Promise<ClientRequest[]> =>
  apiRequest<ClientRequest[]>('/client/requests', {
    channel: 'platform',
    query: {
      status,
      service_type: serviceType,
    },
  });

export const getClientRequest = (id: string): Promise<ClientRequest> =>
  apiRequest<ClientRequest>(`/client/requests/${id}`, { channel: 'platform' });

export const createClientRequest = (payload: Partial<ClientRequest>): Promise<ClientRequest> =>
  apiRequest<ClientRequest>('/client/requests', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateClientRequest = (
  id: string,
  payload: Partial<ClientRequest>
): Promise<ClientRequest> =>
  apiRequest<ClientRequest>(`/client/requests/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const addClientClarification = (
  requestId: string,
  message: string,
  attachmentPath?: string
): Promise<ClientRequestClarification> =>
  apiRequest<ClientRequestClarification>(`/client/requests/${requestId}/clarifications`, {
    method: 'POST',
    body: { message, attachment_path: attachmentPath },
    channel: 'platform',
  });

export const getClientProjects = (status?: string): Promise<ClientProject[]> =>
  apiRequest<ClientProject[]>('/client/projects', {
    channel: 'platform',
    query: { status },
  });

export const getClientProject = (id: string): Promise<ClientProject> =>
  apiRequest<ClientProject>(`/client/projects/${id}`, { channel: 'platform' });

export const createClientProject = (payload: Partial<ClientProject>): Promise<ClientProject> =>
  apiRequest<ClientProject>('/client/projects', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateClientProjectStage = (
  projectId: string,
  stageId: string,
  payload: Partial<ClientProjectStage>
): Promise<ClientProjectStage> =>
  apiRequest<ClientProjectStage>(`/client/projects/${projectId}/stages/${stageId}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const getClientCalendar = (): Promise<ClientCalendarEvent[]> =>
  apiRequest<ClientCalendarEvent[]>('/client/calendar', { channel: 'platform' });

export const createClientCalendarEvent = (
  payload: Partial<ClientCalendarEvent>
): Promise<ClientCalendarEvent> =>
  apiRequest<ClientCalendarEvent>('/client/calendar', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateClientCalendarEvent = (
  id: string,
  payload: Partial<ClientCalendarEvent>
): Promise<ClientCalendarEvent> =>
  apiRequest<ClientCalendarEvent>(`/client/calendar/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const deleteClientCalendarEvent = (id: string): Promise<{ ok: boolean }> =>
  apiRequest<{ ok: boolean }>(`/client/calendar/${id}`, {
    method: 'DELETE',
    channel: 'platform',
  });

export const getClientConnections = (): Promise<ClientConnection[]> =>
  apiRequest<ClientConnection[]>('/client/connections', { channel: 'platform' });

export const createClientConnection = (
  payload: Partial<ClientConnection>
): Promise<ClientConnection> =>
  apiRequest<ClientConnection>('/client/connections', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const sendClientMessage = (
  connectionId: string,
  text: string
): Promise<ClientMessage> =>
  apiRequest<ClientMessage>(`/client/connections/${connectionId}/messages`, {
    method: 'POST',
    body: { text },
    channel: 'platform',
  });

export const getClientNotes = (): Promise<ClientNotebookNote[]> =>
  apiRequest<ClientNotebookNote[]>('/client/notes', { channel: 'platform' });

export const createClientNote = (
  payload: Partial<ClientNotebookNote>
): Promise<ClientNotebookNote> =>
  apiRequest<ClientNotebookNote>('/client/notes', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateClientNote = (
  id: string,
  payload: Partial<ClientNotebookNote>
): Promise<ClientNotebookNote> =>
  apiRequest<ClientNotebookNote>(`/client/notes/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const deleteClientNote = (id: string): Promise<{ ok: boolean }> =>
  apiRequest<{ ok: boolean }>(`/client/notes/${id}`, {
    method: 'DELETE',
    channel: 'platform',
  });

export const getClientFiles = (
  category?: string,
  isResult?: boolean
): Promise<ClientFile[]> =>
  apiRequest<ClientFile[]>('/client/files', {
    channel: 'platform',
    query: {
      category,
      is_result: isResult !== undefined ? String(isResult) : undefined,
    },
  });

export const createClientFile = (payload: Partial<ClientFile>): Promise<ClientFile> =>
  apiRequest<ClientFile>('/client/files', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const deleteClientFile = (id: string): Promise<{ ok: boolean }> =>
  apiRequest<{ ok: boolean }>(`/client/files/${id}`, {
    method: 'DELETE',
    channel: 'platform',
  });

export const runClientTool = (
  toolType: string,
  inputQuery: string
): Promise<ClientToolRunResult> =>
  apiRequest<ClientToolRunResult>('/client/tools/run', {
    method: 'POST',
    body: { tool_type: toolType, input_query: inputQuery },
    channel: 'platform',
  });

export const getClientJointArticles = (): Promise<ClientJointArticle[]> =>
  apiRequest<ClientJointArticle[]>('/client/articles', { channel: 'platform' });

export const createClientJointArticle = (
  payload: Partial<ClientJointArticle>
): Promise<ClientJointArticle> =>
  apiRequest<ClientJointArticle>('/client/articles', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateClientJointArticle = (
  id: string,
  payload: Partial<ClientJointArticle>
): Promise<ClientJointArticle> =>
  apiRequest<ClientJointArticle>(`/client/articles/${id}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const getClientAISuggestion = (
  context: string,
  query: string
): Promise<ClientAISuggestion> =>
  apiRequest<ClientAISuggestion>('/client/ai/consult', {
    method: 'POST',
    body: { context, query },
    channel: 'platform',
  });
